import { Alert, AppBar, Backdrop, Box, Button, ButtonGroup, CircularProgress, FormControl, InputLabel, MenuItem, Modal, Paper, Select, Stack, TextField, Toolbar, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ClassroomInfo } from "../../../../redux/classroom/classroomSlice";
import { HomeworkInfo, HomeworkType } from "../../../../redux/homework/homeworkSlice";
import { emptyHomework, getHomeworkTypeName } from "../../../../redux/homework/homework";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { homeworkInsert, homeworkUpdate, homeworkInsUpdSlice, selectHomeworkInsUpd } from "../../../../redux/homework/homeworkInsUpdSlice";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { LoadState } from "../../../../redux/common";
import { get } from "http";

interface HomeworkModalProps {
    open: boolean;
    onClose: (success: boolean) => void;
    classroom: ClassroomInfo;
    homework?: HomeworkInfo;
}

const HomeworkEditModal = (props: HomeworkModalProps) => {
    const { open, onClose, classroom } = props;
    const homeworkInsUpd = useAppSelector(selectHomeworkInsUpd);

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(homeworkInsUpdSlice.actions.clean());
    }, []);
    useEffect(() => {
        if (homeworkInsUpd.state.state === LoadState.Loaded) {
            dispatch(homeworkInsUpdSlice.actions.clean());
            onClose(true);
        }
    }, [homeworkInsUpd, onClose]);

    const getDefaultStartDateTime = (): Dayjs => {
        if(props.homework?.startDate) {
            try {
                return dayjs(props.homework.startDate);
            } catch (e) {
                console.error("Error parsing start date", e);
            }
        }
        return dayjs().add(0, 'day').set('hour', 0).set('minute', 0).set('second', 0);
    };
    const getDefaultDueDateTime = (): Dayjs => {
        if(props.homework?.dueDate) {
            try {
                return dayjs(props.homework.dueDate);
            } catch (e) {
                console.error("Error parsing due date", e);
            }
        }
        return dayjs().add(7, 'day').set('hour', 0).set('minute', 0).set('second', 0);
    };
    const getDefaultMaxDateTime = (): Dayjs => {
        return dayjs().add(30, 'day').set('hour', 0).set('minute', 0).set('second', 0);
    };

    const getDefaultHomework = (): HomeworkInfo => {
        if(props.homework) {
            return {...props.homework};
        }
        return emptyHomework(classroom.schoolId, classroom.id);
    };
    const [startDate, setStartDate] = useState(getDefaultStartDateTime());
    const [dueDate, setDueDate] = useState(getDefaultDueDateTime());
    const [homework, setHomework] = useState<HomeworkInfo>(
        getDefaultHomework()
    );

    const setHomeworkType = (type: HomeworkType) => {
        setHomework({
            ...homework,
            homeworkType: type
        });
    };

    const isNameValid = (): boolean => {
        return homework.name.trim().length > 4;
    };

    const isStartDateValid = (): boolean => {
        return startDate.isValid() && !dueDate.isBefore(startDate);
    };

    const isDueDateValid = (): boolean => {
        return dueDate.isValid() && !dueDate.isBefore(startDate);
    };

    const isValid = () => {
        return isNameValid() 
            && isStartDateValid() 
            && isDueDateValid();
    };

    const isWorking = () => {
        return homeworkInsUpd.state.state === LoadState.Loading;
    };

    const confirmed = () => {
        homework.startDate = startDate.toISOString();
        homework.dueDate = dueDate.toISOString();
        if(isValid()) {
            if (props.homework) {
                dispatch(homeworkUpdate({homework}));
            } else {
                dispatch(homeworkInsert({homework}));
            }
        }
    };

    return (
        <Modal open={open} onClose={() => onClose(false)}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Paper sx={{ width: "100%", maxWidth: "1000px" }} elevation={3}>
                <AppBar position="static" color={"primary"} sx={{ top: "auto", bottom: 0 }}>
                    <Toolbar>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}> Criar Tarefa </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <ButtonGroup variant="contained" color="secondary">
                            <Tooltip title="Fechar" >
                                <Button key="close" onClick={() => onClose(false)}><CloseIcon /></Button>
                            </Tooltip>
                        </ButtonGroup>
                    </Toolbar>
                </AppBar>
                <Box sx={{width: "100%", px: 5, pt: 1}}>
                    <Typography variant="subtitle1" component="div" sx={{width: "100%", mb: 1}}>
                        Criar uma tarefa para a sala de aula <b>{classroom.name}</b>.
                    </Typography>
                </Box>
                <Box sx={{width: "100%", px: 5, pt: 1}}>
                    <Stack direction={{ xs: "row", sm: "row" }} spacing={2}>
                        <DatePicker
                            value={startDate}
                            label="Data de início"
                            minDate={getDefaultStartDateTime()}
                            maxDate={getDefaultMaxDateTime().isAfter(dueDate) ? dueDate : getDefaultMaxDateTime()}
                            disabled={isWorking()}
                            onChange={(newValue) => {
                                if (newValue) setStartDate(newValue);
                            }}
                            format="DD/MM/YYYY"
                            slotProps={{ 
                                textField: { 
                                    size: "small", 
                                    sx: { width: "150px", backgroundColor: "white" }, 
                                    error: !isStartDateValid(),
                                }
                             }}
                        />
                        <DatePicker
                            value={dueDate}
                            label="Data de entrega"
                            minDate={getDefaultStartDateTime().isAfter(startDate) ? getDefaultStartDateTime() : startDate}
                            maxDate={getDefaultMaxDateTime()}
                            disabled={isWorking()}
                            onChange={(newValue) => {
                                if (newValue) setDueDate(newValue);
                            }}
                            format="DD/MM/YYYY"
                            slotProps={{ 
                                textField: { 
                                    size: "small", 
                                    sx: { width: "150px", backgroundColor: "white" },
                                    error: !isDueDateValid(),
                                } 
                            }}
                        />
                    </Stack>
                </Box>
                <Box sx={{width: "100%", px: 5, pt: 1}}>
                    <FormControl fullWidth sx={{ mt: 1, mb: 0 }}>
                        <InputLabel id="homework-type-label">Tipo de Tarefa</InputLabel>
                        <Select
                            labelId="homework-type-label"
                            id="homework-type-select"
                            value={homework.homeworkType}
                            label="Tipo de Tarefa"
                            disabled={isWorking()}
                            onChange={(e) => {
                                setHomeworkType(Number(e.target.value) as HomeworkType);
                            } }
                        >
                            <MenuItem value={HomeworkType.StudentCheck}>{getHomeworkTypeName(HomeworkType.StudentCheck)}</MenuItem>
                            <MenuItem value={HomeworkType.TeacherReviewed}>{getHomeworkTypeName(HomeworkType.TeacherReviewed)}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{width: "100%", px: 5, pt: 1}}>
                    <TextField key="input-name" size='small' 
                        id="outlined-basic" label="Nome da tarefa" variant="outlined" fullWidth
                        value={homework.name} 
                        error={!isNameValid()}
                        helperText={isNameValid() ? undefined : "Mínimo 5 caracteres"}
                        onChange={(e) => setHomework({ ...homework, name: e.target.value })}
                        disabled={isWorking()}
                    />
                </Box>
                <Box sx={{width: "100%", px: 5, pt: 1}}>
                    <TextField key="input-description" size='small' 
                        id="outlined-basic" label="Descrição" variant="outlined" fullWidth
                        value={homework.description} 
                        multiline
                        rows={7}
                        onChange={(e) => setHomework({ ...homework, description: e.target.value })}
                        disabled={isWorking()}
                    />
                </Box>
                {
                    homeworkInsUpd.state.state === LoadState.Failed &&
                    <Alert severity="error" sx={{ mx: 5, mb: 0 }} hidden={false}>
                        Ocorreu um erro ao salvar a tarefa. Tente novamente.
                    </Alert>
                }
                <Box width="100%" sx={{textAlign: "center", mt: 0, mb: 3}}>
                    <Button 
                        variant='contained' sx={{m:1}} 
                        onClick={() => confirmed()} 
                        disabled={!isValid() || isWorking()}>
                        {
                            props.homework ? "Salvar" : "Criar"
                        }
                    </Button>
                    <Button variant='contained' sx={{m:1}} color='error' 
                        onClick={() => onClose(false)}
                        disabled={isWorking()}>Close</Button>
                </Box>
                <Backdrop open={isWorking()} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Paper>
        </Modal>
    );
};

export default HomeworkEditModal;
